import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import NotificationComponent from "../components/Alert/alert";
import {
  useAddCollectionProductsMutation,
  useCreateCollectionMutation,
  useFetchCollectionByIdQuery,
  useFetchCollectionsQuery,
  useRemoveCollectionProductsMutation,
  useReorderCollectionProductsMutation,
  useSetCollectionStatusMutation,
  useUpdateCollectionMutation,
} from "../api/collectionsApi";
import { getApiErrorMessage } from "../api/errorUtils";
import { useTranslation } from "../hooks/useTranslation";

const slugifyClient = (value) =>
  String(value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");

const emptyForm = {
  name: "",
  slug: "",
  description: "",
  heroImageUrl: "",
  position: "",
  isFeatured: false,
  featuredPosition: "",
};

function CollectionsPage() {
  const t = useTranslation();
  const notificationRef = useRef(null);

  const statusOptions = useMemo(
    () => [
      { value: "DRAFT", label: t("collections.statusDraft"), color: "secondary" },
      { value: "ACTIVE", label: t("collections.statusActive"), color: "success" },
      { value: "ARCHIVED", label: t("collections.statusArchived"), color: "dark" },
    ],
    [t],
  );

  const statusBadgeFor = (value) =>
    statusOptions.find((option) => option.value === value) || statusOptions[0];
  const [editingId, setEditingId] = useState(null);
  const [productsCollectionId, setProductsCollectionId] = useState(null);

  const [formState, setFormState] = useState(emptyForm);
  const [autoSlug, setAutoSlug] = useState(true);

  const [filters, setFilters] = useState({ search: "", status: "" });

  const {
    data: listResult,
    isFetching,
    isError,
    error,
  } = useFetchCollectionsQuery({
    ...(filters.search.trim() ? { search: filters.search.trim() } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    page: 1,
    pageSize: 50,
  });

  const collections = useMemo(() => listResult?.collections || [], [listResult]);

  const [createCollection, createState] = useCreateCollectionMutation();
  const [updateCollection, updateState] = useUpdateCollectionMutation();
  const [setStatus, statusState] = useSetCollectionStatusMutation();

  const formModalOpen = editingId !== null;
  const isCreating = editingId === "new";

  const editingCollection = useMemo(
    () => (isCreating ? null : collections.find((c) => c.id === editingId) || null),
    [collections, editingId, isCreating],
  );

  useEffect(() => {
    if (!formModalOpen) return;
    if (isCreating) {
      setFormState(emptyForm);
      setAutoSlug(true);
      return;
    }
    if (editingCollection) {
      setFormState({
        name: editingCollection.name || "",
        slug: editingCollection.slug || "",
        description: editingCollection.description || "",
        heroImageUrl: editingCollection.heroImageUrl || "",
        position:
          editingCollection.position == null ? "" : String(editingCollection.position),
        isFeatured: Boolean(editingCollection.isFeatured),
        featuredPosition:
          editingCollection.featuredPosition == null
            ? ""
            : String(editingCollection.featuredPosition),
      });
      setAutoSlug(false);
    }
  }, [editingCollection, formModalOpen, isCreating]);

  const closeFormModal = () => setEditingId(null);

  const notify = (type, message) => {
    if (notificationRef.current?.showNotification) {
      notificationRef.current.showNotification(type, message);
    }
  };

  const handleFieldChange = (field) => (event) => {
    const value =
      event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setFormState((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "name" && autoSlug) {
        next.slug = slugifyClient(value);
      }
      return next;
    });
  };

  const handleSlugChange = (event) => {
    setAutoSlug(false);
    setFormState((prev) => ({ ...prev, slug: slugifyClient(event.target.value) }));
  };

  const buildPayload = () => {
    const payload = {
      name: formState.name.trim(),
      slug: formState.slug.trim(),
      description: formState.description.trim() || null,
      heroImageUrl: formState.heroImageUrl.trim() || null,
      isFeatured: Boolean(formState.isFeatured),
    };
    if (formState.position !== "") {
      const parsed = Number.parseInt(formState.position, 10);
      payload.position = Number.isInteger(parsed) ? parsed : null;
    } else {
      payload.position = null;
    }
    if (formState.featuredPosition !== "") {
      const parsed = Number.parseInt(formState.featuredPosition, 10);
      payload.featuredPosition = Number.isInteger(parsed) ? parsed : null;
    } else {
      payload.featuredPosition = null;
    }
    return payload;
  };

  const handleSave = async () => {
    const payload = buildPayload();
    if (!payload.name || payload.name.length < 2) {
      notify("danger", t("collections.nameRequired"));
      return;
    }
    if (!payload.slug || payload.slug.length < 2) {
      notify("danger", t("collections.slugRequired"));
      return;
    }

    try {
      if (isCreating) {
        const result = await createCollection(payload).unwrap();
        notify("success", t("collections.created"));
        setEditingId(null);
        if (result?.id) setProductsCollectionId(result.id);
      } else {
        await updateCollection({ id: editingId, ...payload }).unwrap();
        notify("success", t("collections.updated"));
        setEditingId(null);
      }
    } catch (err) {
      notify("danger", getApiErrorMessage(err) || t("collections.saveError"));
    }
  };

  const handleStatus = async (collection, nextStatus) => {
    if (collection.status === nextStatus) return;
    try {
      await setStatus({ id: collection.id, status: nextStatus }).unwrap();
      notify("success", t("collections.statusUpdated"));
    } catch (err) {
      notify("danger", getApiErrorMessage(err) || t("collections.statusUpdateError"));
    }
  };

  const renderRow = (collection) => {
    const badge = statusBadgeFor(collection.status);
    return (
      <tr key={collection.id}>
        <td>{collection.id}</td>
        <td>
          <strong>{collection.name}</strong>
          {collection.description ? (
            <div className="text-muted text-truncate" style={{ maxWidth: 320 }}>
              {collection.description}
            </div>
          ) : null}
        </td>
        <td>
          <code>{collection.slug}</code>
        </td>
        <td>
          <Badge color={badge.color} pill>
            {badge.label}
          </Badge>
        </td>
        <td>
          {collection.isFeatured ? (
            <Badge color="info" pill>
              {collection.featuredPosition != null
                ? t("collections.featuredPosition").replace(
                    "#{position}",
                    String(collection.featuredPosition),
                  )
                : t("collections.featuredPosition").split(" · ")[0]}
            </Badge>
          ) : (
            <span className="text-muted">—</span>
          )}
        </td>
        <td>{collection.productCount}</td>
        <td align="right">
          <Button
            color="link"
            size="sm"
            title={t("collections.manageProducts")}
            onClick={() => setProductsCollectionId(collection.id)}
          >
            <i className="tim-icons icon-bag-16" />
          </Button>
          <Button
            color="link"
            size="sm"
            title={t("collections.edit")}
            onClick={() => setEditingId(collection.id)}
          >
            <i className="tim-icons icon-pencil" />
          </Button>
          {collection.status !== "ACTIVE" ? (
            <Button
              color="link"
              size="sm"
              title={t("collections.publish")}
              onClick={() => handleStatus(collection, "ACTIVE")}
              disabled={statusState.isLoading}
            >
              <i className="tim-icons icon-check-2" />
            </Button>
          ) : (
            <Button
              color="link"
              size="sm"
              title={t("collections.moveToDraft")}
              onClick={() => handleStatus(collection, "DRAFT")}
              disabled={statusState.isLoading}
            >
              <i className="tim-icons icon-pause" />
            </Button>
          )}
          {collection.status !== "ARCHIVED" ? (
            <Button
              color="link"
              size="sm"
              title={t("collections.archive")}
              onClick={() => handleStatus(collection, "ARCHIVED")}
              disabled={statusState.isLoading}
            >
              <i className="tim-icons icon-trash-simple" />
            </Button>
          ) : null}
        </td>
      </tr>
    );
  };

  return (
    <div className="content">
      <NotificationComponent ref={notificationRef} />

      <Card className="card-chart">
        <CardHeader>
          <Row className="align-items-center">
            <Col className="text-left" sm="6">
              <h5 className="card-category">{t("collections.catalog")}</h5>
              <CardTitle tag="h2">{t("collections.title")}</CardTitle>
            </Col>
            <Col sm="6" className="text-right">
              <Button color="primary" onClick={() => setEditingId("new")}>
                <i className="tim-icons icon-simple-add" /> {t("collections.newCollection")}
              </Button>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col md="6" sm="12">
              <Input
                placeholder={t("collections.searchPlaceholder")}
                value={filters.search}
                onChange={(event) =>
                  setFilters((prev) => ({ ...prev, search: event.target.value }))
                }
              />
            </Col>
            <Col md="3" sm="6" className="mt-2 mt-md-0">
              <Input
                type="select"
                value={filters.status}
                onChange={(event) =>
                  setFilters((prev) => ({ ...prev, status: event.target.value }))
                }
              >
                <option value="">{t("catalogProducts.allStatuses")}</option>
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Input>
            </Col>
          </Row>
        </CardHeader>

        <CardBody>
          {isError ? (
            <div className="text-muted">
              {t("collections.loadError")} {getApiErrorMessage(error) || ""}
            </div>
          ) : null}
          {isFetching && collections.length === 0 ? (
            <div className="text-center py-4">
              <Spinner size="sm" /> {t("collections.loading")}
            </div>
          ) : collections.length === 0 ? (
            <div className="text-center text-muted py-4">
              {t("collections.empty")}
            </div>
          ) : (
            <Table responsive>
              <thead className="text-primary">
                <tr>
                  <th>#</th>
                  <th>{t("collections.colName")}</th>
                  <th>{t("collections.colSlug")}</th>
                  <th>{t("collections.colStatus")}</th>
                  <th>{t("collections.colHomepage")}</th>
                  <th>{t("collections.colProducts")}</th>
                  <th className="text-right">{t("collections.colActions")}</th>
                </tr>
              </thead>
              <tbody>{collections.map(renderRow)}</tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      <Modal isOpen={formModalOpen} toggle={closeFormModal} size="lg">
        <ModalHeader toggle={closeFormModal}>
          {isCreating
            ? t("collections.modalNew")
            : t("collections.modalEdit").replace("#{id}", String(editingId))}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label>{t("collections.fieldName")}</Label>
                <Input value={formState.name} onChange={handleFieldChange("name")} />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>{t("collections.fieldSlug")}</Label>
                <Input
                  value={formState.slug}
                  onChange={handleSlugChange}
                  placeholder={t("collections.slugAuto")}
                />
              </FormGroup>
            </Col>
          </Row>
          <FormGroup>
            <Label>{t("collections.fieldDescription")}</Label>
            <Input
              type="textarea"
              rows={3}
              value={formState.description}
              onChange={handleFieldChange("description")}
              placeholder={t("collections.descriptionHint")}
            />
          </FormGroup>
          <FormGroup>
            <Label>{t("collections.fieldHeroImage")}</Label>
            <Input
              value={formState.heroImageUrl}
              onChange={handleFieldChange("heroImageUrl")}
              placeholder="https://..."
            />
          </FormGroup>
          <Row>
            <Col md="4">
              <FormGroup>
                <Label>{t("collections.fieldPosition")}</Label>
                <Input
                  type="number"
                  min="0"
                  value={formState.position}
                  onChange={handleFieldChange("position")}
                />
              </FormGroup>
            </Col>
            <Col md="4" className="d-flex align-items-end">
              <FormGroup check>
                <Label check>
                  <Input
                    type="checkbox"
                    checked={formState.isFeatured}
                    onChange={handleFieldChange("isFeatured")}
                  />{" "}
                  {t("collections.featuredOnHomepage")}
                </Label>
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label>{t("collections.featuredPositionLabel")}</Label>
                <Input
                  type="number"
                  min="0"
                  value={formState.featuredPosition}
                  onChange={handleFieldChange("featuredPosition")}
                  disabled={!formState.isFeatured}
                />
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="default" onClick={closeFormModal}>
            {t("common.cancel")}
          </Button>
          <Button
            color="primary"
            onClick={handleSave}
            disabled={createState.isLoading || updateState.isLoading}
          >
            {isCreating ? t("commerceCommon.create") : t("commerceCommon.save")}
          </Button>
        </ModalFooter>
      </Modal>

      <CollectionProductsModal
        collectionId={productsCollectionId}
        onClose={() => setProductsCollectionId(null)}
        notify={notify}
      />
    </div>
  );
}

function CollectionProductsModal({ collectionId, onClose, notify }) {
  const t = useTranslation();

  const statusOptions = useMemo(
    () => [
      { value: "DRAFT", label: t("collections.statusDraft"), color: "secondary" },
      { value: "ACTIVE", label: t("collections.statusActive"), color: "success" },
      { value: "ARCHIVED", label: t("collections.statusArchived"), color: "dark" },
    ],
    [t],
  );

  const statusBadgeFor = (value) =>
    statusOptions.find((option) => option.value === value) || statusOptions[0];

  const isOpen = collectionId != null;
  const { data: detail, isFetching } = useFetchCollectionByIdQuery(collectionId, {
    skip: !isOpen,
  });

  const [addProducts, addState] = useAddCollectionProductsMutation();
  const [removeProducts, removeState] = useRemoveCollectionProductsMutation();
  const [reorderProducts, reorderState] = useReorderCollectionProductsMutation();

  const [productIdInput, setProductIdInput] = useState("");
  const [positionInput, setPositionInput] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setProductIdInput("");
      setPositionInput("");
    }
  }, [isOpen]);

  const handleAdd = async () => {
    const id = Number.parseInt(productIdInput, 10);
    if (!Number.isInteger(id) || id <= 0) {
      notify?.("danger", t("collections.invalidProductId"));
      return;
    }
    const position =
      positionInput === "" ? null : Number.parseInt(positionInput, 10);
    try {
      await addProducts({
        id: collectionId,
        products: [{ catalogProductId: id, position }],
      }).unwrap();
      setProductIdInput("");
      setPositionInput("");
      notify?.("success", t("collections.productAdded"));
    } catch (err) {
      notify?.("danger", getApiErrorMessage(err) || t("collections.productAddError"));
    }
  };

  const handleRemove = async (catalogProductId) => {
    try {
      await removeProducts({
        id: collectionId,
        catalogProductIds: [catalogProductId],
      }).unwrap();
      notify?.("success", t("collections.productRemoved"));
    } catch (err) {
      notify?.("danger", getApiErrorMessage(err) || t("collections.productRemoveError"));
    }
  };

  const handlePositionChange = async (catalogProductId, position) => {
    const parsed = Number.parseInt(position, 10);
    if (!Number.isInteger(parsed) || parsed < 0) return;
    try {
      await reorderProducts({
        id: collectionId,
        positions: [{ catalogProductId, position: parsed }],
      }).unwrap();
    } catch (err) {
      notify?.("danger", getApiErrorMessage(err) || t("collections.positionError"));
    }
  };

  const products = detail?.products || [];

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>
        {t("collections.productsIn").replace(
          "{name}",
          detail?.name || t("collections.collectionLabel"),
        )}
      </ModalHeader>
      <ModalBody>
        {isFetching && !detail ? (
          <div className="text-center py-3">
            <Spinner size="sm" /> {t("commerceCommon.loading")}
          </div>
        ) : (
          <>
            <Row className="align-items-end">
              <Col md="5">
                <FormGroup>
                  <Label>{t("collections.catalogProductId")}</Label>
                  <Input
                    type="number"
                    min="1"
                    value={productIdInput}
                    onChange={(event) => setProductIdInput(event.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label>{t("collections.positionOptional")}</Label>
                  <Input
                    type="number"
                    min="0"
                    value={positionInput}
                    onChange={(event) => setPositionInput(event.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md="4">
                <Button
                  color="primary"
                  block
                  onClick={handleAdd}
                  disabled={addState.isLoading}
                >
                  {t("collections.addToCollection")}
                </Button>
              </Col>
            </Row>

            {products.length === 0 ? (
              <div className="text-center text-muted py-3">
                {t("collections.noProductsInCollection")}
              </div>
            ) : (
              <Table responsive>
                <thead className="text-primary">
                  <tr>
                    <th>{t("collections.fieldPosition")}</th>
                    <th>{t("collections.colCatalogProduct")}</th>
                    <th>{t("commerceCommon.status")}</th>
                    <th className="text-right">{t("collections.colActions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr key={product.catalogProductId}>
                      <td style={{ width: 120 }}>
                        <Input
                          type="number"
                          min="0"
                          defaultValue={product.position ?? ""}
                          disabled={reorderState.isLoading}
                          onBlur={(event) =>
                            handlePositionChange(
                              product.catalogProductId,
                              event.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <strong>{product.title}</strong>
                        <div className="text-muted">
                          <code>{product.slug}</code> · #{product.catalogProductId}
                        </div>
                      </td>
                      <td>
                        <Badge
                          color={statusBadgeFor(product.status).color}
                          pill
                        >
                          {statusBadgeFor(product.status).label}
                        </Badge>
                      </td>
                      <td align="right">
                        <Button
                          color="link"
                          size="sm"
                          title={t("collections.remove")}
                          disabled={removeState.isLoading}
                          onClick={() => handleRemove(product.catalogProductId)}
                        >
                          <i className="tim-icons icon-simple-remove" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="default" onClick={onClose}>
          {t("commerceCommon.close")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default CollectionsPage;
